/**
 * Exportar um guia a partir da web: HTML autónomo, Markdown (ZIP com as imagens),
 * PowerPoint e impressão/PDF; e todos os guias de um espaço num ZIP (exportSpaceZip). As
 * capturas saem já com o zoom e o marcador; são pedidas e desenhadas 4 de cada vez, e quem
 * exporta recebe o progresso (onProgress).
 */
import {
  buildEditorialHtml,
  buildMarkdown,
  makeZip,
  PRODUCT,
  stepNumbers,
  type BrandSettings,
  type GuideBrand,
  type GuideSummary,
  type RemoteGuide,
  type Step,
  type Locale,
} from '@nolens/domain';
import { blobToDataUrl, download, renderStepBlob, slug } from '@nolens/ui';
import { api, apiBlob } from './api';
import { buildDeck, type DeckImage } from './pptx';

interface Source {
  id: string;
  title: string;
  description: string;
  language: Locale;
  steps: Step[];
  workspaceId: string;
}

export interface ExportOptions {
  /** Exportar sem as capturas que não carregaram (a pessoa já o aceitou). */
  skipMissing?: boolean;
  /** Capturas prontas de quantas (para mostrar o progresso). */
  onProgress?: ((done: number, total: number) => void) | undefined;
}

/** Capturas pedidas e desenhadas ao mesmo tempo. */
const PARALLEL = 4;

/**
 * Há capturas que não carregaram, mesmo à 2.ª tentativa (sem rede, sessão terminada…).
 * Um manual sem algumas imagens não sai sem a pessoa saber: quem exporta pergunta-lhe.
 */
export class MissingImages extends Error {
  constructor(
    readonly missing: number,
    readonly total: number,
  ) {
    super(`${missing} of ${total} screenshots could not be loaded`);
  }
}

/** Nome dos ficheiros exportados: o do guia (sem título, «guide» ou «guia», na língua dele). */
const fileName = (g: Source) => slug(g.title, g.language === 'en' ? 'guide' : 'guia');

/** Uma captura, com uma 2.ª tentativa (uma falha breve da rede não tira a imagem ao manual). */
async function imageOf(path: string): Promise<Blob | null> {
  try {
    return await apiBlob(path);
  } catch {
    await new Promise((r) => setTimeout(r, 800));
    return apiBlob(path).catch(() => null);
  }
}

async function stepImages(
  g: Source,
  accent: string | undefined,
  opts: ExportOptions,
): Promise<Map<string, Blob>> {
  const shots = g.steps.filter((s) => s.imageId);
  const ready: (Blob | null)[] = new Array(shots.length).fill(null);
  let next = 0;
  let done = 0;
  opts.onProgress?.(0, shots.length);
  const worker = async () => {
    while (next < shots.length) {
      const i = next++;
      const s = shots[i]!;
      const raw = await imageOf(`/v1/guides/${g.id}/images/${s.imageId}`);
      ready[i] = raw ? await renderStepBlob(s, raw, { accent }) : null;
      opts.onProgress?.(++done, shots.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, shots.length) }, worker));
  // Pela ordem dos passos (chegam por outra ordem)
  const out = new Map<string, Blob>();
  shots.forEach((s, i) => ready[i] && out.set(s.id, ready[i]));
  const missing = shots.length - out.size;
  if (missing && !opts.skipMissing) throw new MissingImages(missing, shots.length);
  return out;
}

async function brandFor(
  g: Source,
  b: BrandSettings | null,
  name: string,
): Promise<Partial<GuideBrand>> {
  if (!b) return {};
  const logo = b.logoVersion
    ? await apiBlob(`/v1/workspaces/${g.workspaceId}/brand/logo`).then(blobToDataUrl, () => null)
    : null;
  const fontUrl =
    b.font === 'custom' && b.fontVersion
      ? await apiBlob(`/v1/workspaces/${g.workspaceId}/brand/font`).then(
          (x) => blobToDataUrl(new Blob([x], { type: 'font/woff2' })),
          () => null,
        )
      : null;
  return {
    accent: b.accent,
    font: b.font,
    footer: b.footer,
    logo,
    fontUrl,
    name: b.companyName.trim() || name,
    coverColor: b.coverColor,
    highlightColor: b.highlightColor,
  };
}

export async function htmlOf(
  g: Source,
  b: BrandSettings | null,
  workspaceName: string,
  opts: ExportOptions = {},
): Promise<string> {
  const imgs = await stepImages(g, b?.accent, opts);
  const images: Record<string, string | null> = {};
  for (const [id, blob] of imgs) images[id] = await blobToDataUrl(blob);
  const brand = b ? await brandFor(g, b, workspaceName) : { name: workspaceName };
  return buildEditorialHtml(g, images, { brand });
}

export async function exportHtml(
  g: Source,
  b: BrandSettings | null,
  workspaceName: string,
  opts: ExportOptions = {},
) {
  download(
    new Blob([await htmlOf(g, b, workspaceName, opts)], { type: 'text/html' }),
    fileName(g) + '.html',
  );
}

export async function printPdf(
  g: Source,
  b: BrandSettings | null,
  workspaceName: string,
  opts: ExportOptions = {},
) {
  const html = await htmlOf(g, b, workspaceName, opts);
  const fr = document.createElement('iframe');
  fr.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(fr);
  fr.srcdoc = html;
  await new Promise((r) => (fr.onload = r));
  fr.contentWindow?.print();
  setTimeout(() => fr.remove(), 60_000);
}

export async function exportMarkdown(g: Source, b: BrandSettings | null, opts: ExportOptions = {}) {
  const imgs = await stepImages(g, b?.accent, opts);
  const numbers = stepNumbers(g);
  const paths: Record<string, string | null> = {};
  const files: { name: string; data: Uint8Array }[] = [];
  for (const [id, blob] of imgs) {
    const name = `images/${String(numbers.get(id) ?? 0).padStart(2, '0')}.jpg`;
    paths[id] = name;
    files.push({ name, data: new Uint8Array(await blob.arrayBuffer()) });
  }
  const md = buildMarkdown(g, paths);
  // O Markdown com o nome do guia (antes era sempre «guia.md», também nos guias em inglês)
  const zip = makeZip([
    { name: fileName(g) + '.md', data: new TextEncoder().encode(md) },
    ...files,
  ]);
  download(new Blob([zip], { type: 'application/zip' }), fileName(g) + '.zip');
}

/** A partir deste tamanho, o ZIP de um espaço passa para uma parte nova (memória do browser). */
const PART_BYTES = 200 * 1024 * 1024;
/** Guias por exportação de um espaço (o máximo da lista da API). */
export const SPACE_EXPORT_MAX = 500;

/**
 * Exportar todos os guias de um espaço é para quando o serviço fechar (termos, «Se o serviço
 * fechar»): até lá só o proprietário da aplicação a vê. Para a abrir a toda a gente, passar a
 * true e publicar (docs/operacao.md, «Encerrar o serviço»).
 */
export const SPACE_EXPORT_FOR_ALL = false;

export const canExportSpace = (me: { isPlatformAdmin: boolean }) =>
  SPACE_EXPORT_FOR_ALL || me.isPlatformAdmin;

export interface SpaceExport {
  guides: number;
  parts: number;
  /** Capturas que não carregaram (ficaram de fora). */
  missing: number;
}

/**
 * Todos os guias de um espaço num ZIP: uma pasta por guia, com o Markdown e as capturas, e um
 * índice na raiz. Com muitas capturas, divide-se em partes de cerca de 200 MB, descarregadas
 * uma a uma. As capturas que não carregarem ficam de fora (o resultado diz quantas).
 */
export async function exportSpaceZip(
  ws: { id: string; name: string },
  locale: Locale,
  onProgress?: (done: number, total: number) => void,
): Promise<SpaceExport> {
  const list = await api<GuideSummary[]>(`/v1/guides?workspace=${ws.id}&limit=${SPACE_EXPORT_MAX}`);
  const pt = locale === 'pt-PT';
  const base = slug(ws.name, pt ? 'guias' : 'guides');
  const used = new Set<string>();
  let files: { name: string; data: Uint8Array }[] = [];
  let index: string[] = [];
  let size = 0;
  let parts = 0;
  let missing = 0;
  const flush = (last: boolean) => {
    const readme = [
      `# ${ws.name}`,
      '',
      pt
        ? `Guias exportados do Nolens Guides a ${new Date().toLocaleDateString(locale)}${last && parts === 0 ? '' : ` (parte ${parts + 1})`}.`
        : `Guides exported from Nolens Guides on ${new Date().toLocaleDateString(locale)}${last && parts === 0 ? '' : ` (part ${parts + 1})`}.`,
      '',
      ...index,
      '',
    ].join('\n');
    const zip = makeZip([
      { name: pt ? 'LEIA-ME.md' : 'README.md', data: new TextEncoder().encode(readme) },
      ...files,
    ]);
    const name = last && parts === 0 ? `${base}.zip` : `${base}-${parts + 1}.zip`;
    download(new Blob([zip], { type: 'application/zip' }), name);
    parts++;
    files = [];
    index = [];
    size = 0;
  };
  onProgress?.(0, list.length);
  for (const [n, summary] of list.entries()) {
    const g = await api<RemoteGuide>(`/v1/guides/${summary.id}`);
    const imgs = await stepImages(g, undefined, { skipMissing: true });
    missing += g.steps.filter((s) => s.imageId).length - imgs.size;
    let folder = fileName(g);
    for (let k = 2; used.has(folder); k++) folder = `${fileName(g)}-${k}`;
    used.add(folder);
    const numbers = stepNumbers(g);
    const paths: Record<string, string | null> = {};
    for (const [id, blob] of imgs) {
      const name = `images/${String(numbers.get(id) ?? 0).padStart(2, '0')}.jpg`;
      paths[id] = name;
      const data = new Uint8Array(await blob.arrayBuffer());
      files.push({ name: `${folder}/${name}`, data });
      size += data.byteLength;
    }
    files.push({
      name: `${folder}/${folder}.md`,
      data: new TextEncoder().encode(buildMarkdown(g, paths)),
    });
    index.push(`- [${g.title.replace(/[[\]]/g, '')}](${folder}/${folder}.md)`);
    onProgress?.(n + 1, list.length);
    if (size >= PART_BYTES && n < list.length - 1) flush(false);
  }
  if (list.length) flush(true);
  return { guides: list.length, parts, missing };
}

/** PowerPoint no formato editorial (capa, sumário, passos, fecho), com o kit de marca. */
export async function exportPptx(
  g: Source,
  b: BrandSettings | null,
  workspaceName: string,
  opts: ExportOptions = {},
) {
  const { default: PptxGenJS } = await import('pptxgenjs');
  const imgs = await stepImages(g, b?.accent, opts);
  const sized = async (blob: Blob): Promise<DeckImage> => {
    const bmp = await createImageBitmap(blob);
    const out = { data: await blobToDataUrl(blob), w: bmp.width, h: bmp.height };
    bmp.close();
    return out;
  };
  const images = new Map<string, DeckImage>();
  for (const [id, blob] of imgs) images.set(id, await sized(blob));
  const logo = b?.logoVersion
    ? await apiBlob(`/v1/workspaces/${g.workspaceId}/brand/logo`).then(sized, () => null)
    : null;
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_16x9'; // 10 × 5,625 pol.
  pptx.title = g.title;
  buildDeck(pptx, {
    title: g.title,
    description: g.description,
    language: g.language,
    steps: g.steps,
    numbers: stepNumbers(g),
    images,
    accent: b?.accent ?? PRODUCT.accent,
    coverColor: b?.coverColor ?? null,
    highlightColor: b?.highlightColor ?? null,
    font: b?.font,
    brandName: b?.companyName.trim() || workspaceName || 'Nolens ' + PRODUCT.name,
    logo,
    date: new Date().toLocaleDateString(g.language),
  });
  const data = (await pptx.write({ outputType: 'blob' })) as Blob;
  download(data, fileName(g) + '.pptx');
}

/** Código para incorporar (precisa da ligação pública ligada). */
export const embedCode = (origin: string, token: string, title: string) =>
  `<iframe src="${origin}/e/${token}" title="${title.replace(/[<>"&]/g, '')}" width="100%" height="720" style="border:1px solid #e7e6e2;border-radius:12px" loading="lazy" referrerpolicy="no-referrer"></iframe>`;
